import { Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxExtendedPdfViewerModule, PdfLoadedEvent } from 'ngx-extended-pdf-viewer';

import { DocumentPipelineService } from '../../../services/document-pipeline.service';
import { DocumentViewerController, DocumentViewerService } from '../../../services/document-viewer.service';
import { DocumentViewerZoomSteps } from '../../../constants/document-viewer-zoom';

/**
 * On-screen preview for the unified document pipeline: shows the SAME
 * server-rendered PDF that Download/Print/Email use, so what the user sees is
 * exactly what every other channel produces.
 *
 * Rendering uses pdf.js (ngx-extended-pdf-viewer) with its stock toolbar
 * hidden: the page/zoom controls live in the report toolbar's strip instead
 * (via DocumentViewerService), so the selector, viewer controls and the
 * audited Email/Download/Print actions form one row above a full-width
 * document. Consumers only pass documentType/reportId.
 */
@Component({
  selector: 'app-document-preview',
  templateUrl: './document-preview.component.html',
  styleUrl: './document-preview.component.scss',
  imports: [NgxExtendedPdfViewerModule],
})
export class DocumentPreviewComponent {
  private readonly documentPipelineService = inject(DocumentPipelineService);
  private readonly documentViewerService = inject(DocumentViewerService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly documentType = input.required<string>();
  public readonly reportId = input.required<string>();
  public readonly title = input('');

  protected readonly pdfSrc = signal<Blob | undefined>(undefined);
  protected readonly loading = signal(false);
  protected readonly error = signal('');

  protected readonly page = signal(1);
  protected readonly pageCount = signal(0);
  protected readonly zoom = signal<number | string>(100);
  /** Last numeric zoom, so +/- have a base to step from while in fit-width mode. */
  private numericZoom = 100;

  constructor() {
    effect(() => {
      const documentType = this.documentType();
      const reportId = this.reportId();
      if (documentType && reportId) {
        this.load(documentType, reportId);
      }
    });

    const controller: DocumentViewerController = {
      page: this.page.asReadonly(),
      pageCount: this.pageCount.asReadonly(),
      zoomLabel: computed(() => {
        const zoom = this.zoom();
        return typeof zoom === 'number' ? `${zoom}%` : $localize`Fit`;
      }),
      setPage: (page: number) => this.setPage(page),
      previousPage: () => this.setPage(this.page() - 1),
      nextPage: () => this.setPage(this.page() + 1),
      zoomIn: () => this.stepZoom(1),
      zoomOut: () => this.stepZoom(-1),
      fitWidth: () => this.zoom.set('page-width'),
    };
    effect(() => {
      if (this.pdfSrc()) {
        this.documentViewerService.register(controller);
      } else {
        this.documentViewerService.unregister(controller);
      }
    });
    this.destroyRef.onDestroy(() => this.documentViewerService.unregister(controller));
  }

  reload() {
    this.load(this.documentType(), this.reportId());
  }

  protected onPdfLoaded(event: PdfLoadedEvent) {
    this.pageCount.set(event.pagesCount);
    this.page.set(1);
  }

  protected onZoomChange(zoom: number | string) {
    if (typeof zoom === 'number' && zoom > 0) {
      this.numericZoom = Math.round(zoom);
    }
  }

  private setPage(page: number) {
    if (!page || page < 1 || page > this.pageCount()) {
      return;
    }
    this.page.set(page);
  }

  private stepZoom(direction: 1 | -1) {
    const next =
      direction === 1
        ? DocumentViewerZoomSteps.find((step) => step > this.numericZoom)
        : [...DocumentViewerZoomSteps].reverse().find((step) => step < this.numericZoom);
    if (next) {
      this.numericZoom = next;
      this.zoom.set(next);
    }
  }

  private load(documentType: string, reportId: string) {
    this.loading.set(true);
    this.error.set('');
    this.pdfSrc.set(undefined);

    this.documentPipelineService
      .preview(documentType, reportId, this.title() || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob: Blob) => {
          this.page.set(1);
          this.pageCount.set(0);
          this.pdfSrc.set(blob);
          this.loading.set(false);
        },
        error: async (err: unknown) => {
          this.loading.set(false);
          this.error.set(
            await this.documentPipelineService.getErrorMessage(
              err,
              $localize`The document preview could not be generated. Please try again.`,
            ),
          );
        },
      });
  }
}
